import React, { useState, useEffect, useCallback } from 'react';
import {
  FiHelpCircle, FiMessageSquare, FiBook, FiChevronDown, FiChevronUp,
  FiPlus, FiLoader, FiSearch, FiMail, FiPhone, FiClock, FiCheck,
  FiAlertCircle, FiX, FiExternalLink, FiFileText, FiSend, FiRefreshCw
} from 'react-icons/fi';
import { supportApi } from '../services/api';

const HelpSupport = () => {
  const [activeTab, setActiveTab] = useState('faq');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // FAQ State
  const [faqs, setFaqs] = useState([]);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [faqSearch, setFaqSearch] = useState('');
  
  // Tickets State
  const [tickets, setTickets] = useState([]);
  const [ticketStats, setTicketStats] = useState({ open: 0, pending: 0, resolved: 0 });
  const [categories, setCategories] = useState([]);
  
  // New Ticket Form
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    description: '',
    category: 'General',
    priority: 'Medium'
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [faqData, ticketData, statsData, categoriesData] = await Promise.all([
        supportApi.getFAQs(),
        supportApi.getTickets(1),
        supportApi.getTicketStats(1),
        supportApi.getCategories()
      ]);
      
      setFaqs(faqData);
      const normalizedTickets = Array.isArray(ticketData?.items)
        ? ticketData.items
        : Array.isArray(ticketData)
          ? ticketData
          : [];
      setTickets(normalizedTickets);
      setTicketStats(statsData);
      setCategories(categoriesData);
    } catch (err) {
      console.error('Failed to fetch support data:', err);
      // Use defaults
      setFaqs([
        { id: 1, question: 'How do I create a new risk assessment?', answer: 'Navigate to the Assessments page and click the "New Assessment" button. Follow the wizard to complete the assessment.', category: 'Assessments' },
        { id: 2, question: 'How do I export reports?', answer: 'Go to the Reports section, select the report you want to export, and click the export button. You can choose between PDF, Excel, or CSV formats.', category: 'Reports' },
        { id: 3, question: 'How do I manage user permissions?', answer: 'Navigate to Users page, select the user, and click Edit. You can modify their role and permissions from the edit modal.', category: 'Users' },
        { id: 4, question: 'How do I enable dark mode?', answer: 'Click on the Settings icon in the header and toggle the dark mode option, or go to Settings > Appearance.', category: 'General' },
        { id: 5, question: 'How do I reset my password?', answer: 'Go to Settings > Security and use the Change Password form. You\'ll need to enter your current password and the new password.', category: 'Security' }
      ]);
      setCategories(['General', 'Technical', 'Billing', 'Account', 'Feature Request']);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    if (!ticketForm.subject || !ticketForm.description) return;
    
    setSubmitting(true);
    try {
      await supportApi.createTicket({
        ...ticketForm,
        userId: 1,
        submittedBy: 'Current User'
      });
      setShowTicketForm(false);
      setTicketForm({ subject: '', description: '', category: 'General', priority: 'Medium' });
      await fetchData();
    } catch (err) {
      console.error('Failed to submit ticket:', err);
      alert('Failed to submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(faqSearch.toLowerCase()) ||
    faq.answer.toLowerCase().includes(faqSearch.toLowerCase()) ||
    faq.category?.toLowerCase().includes(faqSearch.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      Open: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
      Pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300',
      Resolved: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
      Closed: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
    };
    return styles[status] || styles.Open;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      Low: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
      Medium: 'bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-300',
      High: 'bg-orange-100 text-orange-600 dark:bg-orange-900/50 dark:text-orange-300',
      Critical: 'bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-300'
    };
    return styles[priority] || styles.Medium;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FiLoader className="w-8 h-8 animate-spin text-blue-500" />
        <span className={`ml-2 text-slate-600`}>Loading...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold text-slate-800`}>Help & Support</h1>
          <p className={`mt-1 text-slate-500`}>Find answers and get help with your questions</p>
        </div>
        <button
          onClick={() => setShowTicketForm(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#ff6600] text-white rounded-lg text-sm font-medium hover:bg-[#e65c00] transition-colors"
        >
          <FiPlus className="w-4 h-4" />
          Create Support Ticket
        </button>
      </div>

      {/* Quick Contact Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`p-5 rounded-xl border bg-white border-slate-200`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
              <FiMail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className={`font-medium text-slate-800`}>Email Support</p>
              <p className={`text-sm text-slate-500`}>support@trustcenter.com</p>
            </div>
          </div>
        </div>
        
        <div className={`p-5 rounded-xl border bg-white border-slate-200`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/50 rounded-lg flex items-center justify-center">
              <FiPhone className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className={`font-medium text-slate-800`}>Phone Support</p>
              <p className={`text-sm text-slate-500`}>+1 (555) 123-4567</p>
            </div>
          </div>
        </div>
        
        <div className={`p-5 rounded-xl border bg-white border-slate-200`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#fff3e6] dark:bg-orange-900/50 rounded-lg flex items-center justify-center">
              <FiClock className="w-5 h-5 text-[#ff6600] dark:text-orange-400" />
            </div>
            <div>
              <p className={`font-medium text-slate-800`}>Response Time</p>
              <p className={`text-sm text-slate-500`}>Within 24 hours</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={`border-b border-slate-200`}>
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('faq')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'faq'
                ? 'border-[#ff6600] text-[#ff6600]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <FiHelpCircle className="inline-block mr-2" />
            FAQs
          </button>
          <button
            onClick={() => setActiveTab('tickets')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'tickets'
                ? 'border-[#ff6600] text-[#ff6600]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <FiMessageSquare className="inline-block mr-2" />
            My Tickets
            {ticketStats.open > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-[#ff6600] text-white rounded-full">{ticketStats.open}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('resources')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'resources'
                ? 'border-[#ff6600] text-[#ff6600]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <FiBook className="inline-block mr-2" />
            Resources
          </button>
        </nav>
      </div>

      {/* FAQ Tab */}
      {activeTab === 'faq' && (
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <FiSearch className={`absolute left-4 top-1/2 -translate-y-1/2 text-slate-400`} />
            <input
              type="text"
              placeholder="Search FAQs..."
              value={faqSearch}
              onChange={(e) => setFaqSearch(e.target.value)}
              className={`w-full pl-11 pr-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800 placeholder-slate-400`}
            />
          </div>

          {/* FAQ List */}
          <div className={`rounded-xl border overflow-hidden bg-white border-slate-200`}>
            {filteredFaqs.length === 0 ? (
              <div className={`p-8 text-center text-slate-500`}>
                <FiHelpCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No FAQs found matching your search.</p>
              </div>
            ) : (
              filteredFaqs.map((faq, index) => (
                <div
                  key={faq.id}
                  className={`border-b last:border-b-0 border-slate-200`}
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                    className={`w-full flex items-center justify-between p-4 text-left transition-colors hover:bg-slate-50`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-1 rounded bg-slate-100 text-slate-600`}>
                        {faq.category}
                      </span>
                      <span className={`font-medium text-slate-800`}>{faq.question}</span>
                    </div>
                    {expandedFaq === faq.id ? (
                      <FiChevronUp className={`w-5 h-5 text-slate-500`} />
                    ) : (
                      <FiChevronDown className={`w-5 h-5 text-slate-500`} />
                    )}
                  </button>
                  {expandedFaq === faq.id && (
                    <div className={`px-4 pb-4 text-slate-600`}>
                      <p className="pl-10">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Tickets Tab */}
      {activeTab === 'tickets' && (
        <div className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className={`p-4 rounded-lg bg-blue-50`}>
              <div className="flex items-center justify-between">
                <span className={`text-sm text-blue-600`}>Open</span>
                <span className={`text-xl font-bold text-blue-700`}>{ticketStats.open}</span>
              </div>
            </div>
            <div className={`p-4 rounded-lg bg-yellow-50`}>
              <div className="flex items-center justify-between">
                <span className={`text-sm text-yellow-600`}>Pending</span>
                <span className={`text-xl font-bold text-yellow-700`}>{ticketStats.pending}</span>
              </div>
            </div>
            <div className={`p-4 rounded-lg bg-green-50`}>
              <div className="flex items-center justify-between">
                <span className={`text-sm text-green-600`}>Resolved</span>
                <span className={`text-xl font-bold text-green-700`}>{ticketStats.resolved}</span>
              </div>
            </div>
          </div>

          {/* Tickets List */}
          <div className={`rounded-xl border overflow-hidden bg-white border-slate-200`}>
            <div className={`px-4 py-3 flex items-center justify-between border-b border-slate-200`}>
              <h3 className={`font-medium text-slate-800`}>Support Tickets</h3>
              <button
                onClick={fetchData}
                className={`p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-slate-500`}
              >
                <FiRefreshCw className="w-4 h-4" />
              </button>
            </div>
            
            { (Array.isArray(tickets) ? tickets.length : 0) === 0 ? (
              <div className={`p-8 text-center text-slate-500`}>
                <FiMessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No tickets found. Create one to get help!</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-200 dark:divide-slate-700">
                {(Array.isArray(tickets) ? tickets : []).map(ticket => (
                  <div key={ticket.id} className={`p-4 hover:bg-slate-50 transition-colors`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs text-slate-400`}>#{ticket.id}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${getStatusBadge(ticket.status)}`}>
                            {ticket.status}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded ${getPriorityBadge(ticket.priority)}`}>
                            {ticket.priority}
                          </span>
                        </div>
                        <h4 className={`font-medium text-slate-800`}>{ticket.subject}</h4>
                        <p className={`text-sm mt-1 text-slate-500`}>{ticket.description}</p>
                      </div>
                      <div className={`text-xs text-slate-400`}>
                        {new Date(ticket.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Resources Tab */}
      {activeTab === 'resources' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <a
            href="#"
            className={`p-5 rounded-xl border flex items-center gap-4 transition-colors bg-white border-slate-200 hover:bg-slate-50`}
          >
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
              <FiBook className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className={`font-medium text-slate-800`}>User Guide</h3>
              <p className={`text-sm text-slate-500`}>Comprehensive documentation for all features</p>
            </div>
            <FiExternalLink className={`ml-auto text-slate-400`} />
          </a>

          <a
            href="#"
            className={`p-5 rounded-xl border flex items-center gap-4 transition-colors bg-white border-slate-200 hover:bg-slate-50`}
          >
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/50 rounded-lg flex items-center justify-center">
              <FiFileText className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className={`font-medium text-slate-800`}>API Documentation</h3>
              <p className={`text-sm text-slate-500`}>Technical reference for developers</p>
            </div>
            <FiExternalLink className={`ml-auto text-slate-400`} />
          </a>

          <a
            href="#"
            className={`p-5 rounded-xl border flex items-center gap-4 transition-colors bg-white border-slate-200 hover:bg-slate-50`}
          >
            <div className="w-12 h-12 bg-[#fff3e6] dark:bg-orange-900/50 rounded-lg flex items-center justify-center">
              <FiMessageSquare className="w-6 h-6 text-[#ff6600] dark:text-orange-400" />
            </div>
            <div>
              <h3 className={`font-medium text-slate-800`}>Video Tutorials</h3>
              <p className={`text-sm text-slate-500`}>Step-by-step video walkthroughs</p>
            </div>
            <FiExternalLink className={`ml-auto text-slate-400`} />
          </a>

          <a
            href="#"
            className={`p-5 rounded-xl border flex items-center gap-4 transition-colors bg-white border-slate-200 hover:bg-slate-50`}
          >
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/50 rounded-lg flex items-center justify-center">
              <FiAlertCircle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h3 className={`font-medium text-slate-800`}>Release Notes</h3>
              <p className={`text-sm text-slate-500`}>Latest updates and changes</p>
            </div>
            <FiExternalLink className={`ml-auto text-slate-400`} />
          </a>
        </div>
      )}

      {/* Create Ticket Modal */}
      {showTicketForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowTicketForm(false)}>
              <div className="absolute inset-0 bg-black/50"></div>
            </div>

            <div className={`relative inline-block w-full max-w-lg p-6 overflow-hidden text-left align-bottom rounded-2xl shadow-xl transform transition-all sm:align-middle bg-white`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold text-slate-800`}>Create Support Ticket</h3>
                <button
                  onClick={() => setShowTicketForm(false)}
                  className={`p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500`}
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmitTicket} className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 text-slate-700`}>Subject *</label>
                  <input
                    type="text"
                    value={ticketForm.subject}
                    onChange={(e) => setTicketForm(prev => ({ ...prev, subject: e.target.value }))}
                    className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                    placeholder="Brief summary of your issue"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 text-slate-700`}>Category</label>
                    <select
                      value={ticketForm.category}
                      onChange={(e) => setTicketForm(prev => ({ ...prev, category: e.target.value }))}
                      className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 text-slate-700`}>Priority</label>
                    <select
                      value={ticketForm.priority}
                      onChange={(e) => setTicketForm(prev => ({ ...prev, priority: e.target.value }))}
                      className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 text-slate-700`}>Description *</label>
                  <textarea
                    rows={4}
                    value={ticketForm.description}
                    onChange={(e) => setTicketForm(prev => ({ ...prev, description: e.target.value }))}
                    className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white border-slate-200 text-slate-800`}
                    placeholder="Detailed description of your issue or question"
                    required
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowTicketForm(false)}
                    className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors bg-slate-100 text-slate-700 hover:bg-slate-200`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2.5 bg-[#ff6600] text-white rounded-lg text-sm font-medium hover:bg-[#e65c00] transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {submitting ? <FiLoader className="animate-spin" /> : <FiSend />}
                    {submitting ? 'Submitting...' : 'Submit Ticket'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpSupport;
